"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { noop } from 'utils/noop';

const defaultMovingTowardSubmenContext = [false, noop] as [
  string | false,
  React.Dispatch<React.SetStateAction<string | false>>
];

const MovingTowardSubmenuContext = createContext(
  defaultMovingTowardSubmenContext
);

/**
 * Context provider component for a group of list items
 * which could potentially have their selection state
 * temporarily prevented.
 */

export function MovingTowardSubmenuProvider({ children }: { children: React.ReactNode }) {
  const preventSelection = useState<false | string>(false);

  return (
    <MovingTowardSubmenuContext.Provider value={preventSelection}>
      {children}
    </MovingTowardSubmenuContext.Provider>
  );
}

type Point = {
  x: number;
  y: number;
};

/**
 * Determine the angle between two points
 * @param {{x, y}} p1 Point 1
 * @param {{x, y}} p2 Point 2
 */

export function getAngle(p1: Point, p2: Point) {
  return (Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180) / Math.PI;
}

/**
 * Determine if the user is likely moving towards an open submenu. This
 * function tries to determine the angle from the cursor position
 * to the top and bottom bounds of the submenu. If the cursor
 * is moving between those bounds, we assume the user is moving
 * towards the submenu.
 *
 * @param {{x: Number, y: Number}} prevCoordinates max-length of 5
 * @param {Event} e onMouseMove event
 * @param {Object} submenuBounds boundingClientRect of expanded submenu
 */

type SubmenuBounds = {
  isLeft: boolean;
  top: number;
  left: number;
  right: number;
  bottom: number;
};

function determineIfMovingTowardSubmenu(
  previousPoint: Point,
  e: React.MouseEvent,
  submenuBounds: SubmenuBounds | null
) {
  if (!submenuBounds || !previousPoint) {
    return false;
  }

  const currentAngle = getAngle(previousPoint, { x: e.clientX, y: e.clientY });
  const { isLeft, top, left, right, bottom } = submenuBounds;

  if (currentAngle === 0) {
    return isLeft ? e.clientX < previousPoint.x : e.clientX > previousPoint.x;
  }

  const x = isLeft ? right : left;
  const bottomAngle = getAngle(previousPoint, { x, y: bottom });
  const topAngle = getAngle(previousPoint, { x, y: top });

  if (isLeft) {
    // For left-positioned submenus, angles may wrap across -180/180.
    if (topAngle > bottomAngle) {
      return currentAngle >= bottomAngle && currentAngle <= topAngle;
    }
    return currentAngle >= bottomAngle || currentAngle <= topAngle;
  }

  return currentAngle < bottomAngle && currentAngle > topAngle;
}

export type UseMovingTowardSubmenuProps = {
  delay?: number;
  id: string;
  enabled: boolean;
  popoverRef: React.MutableRefObject<HTMLDivElement>;
};

/**
 * Track mouse movements and determine if the user is moving
 * towards a submenu upon the cursor leaving the target. This is used
 * to determine if a submenu should collapse when the cursor
 * leave the target element.
 */

export function useMovingTowardSubmenu({
  delay = 200,
  id,
  enabled = false,
  popoverRef,
}: UseMovingTowardSubmenuProps) {
  const isMovingTowardSubmenu = useRef(false);
  const previousMouseCoordinates = useRef<Point>(null);
  const timer = useRef<number>(null);
  const boundsRef = useRef<SubmenuBounds>(null);
  const [preventSelection, setPreventSelection] = useContext(
    MovingTowardSubmenuContext
  );

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  /**
   * if the user quickly moves their cursor towards the submenu,
   * but then returns their cursor to this MenuItem, reset
   * the movement state.
   */

  const onMouseEnter = useCallback(() => {
    if (preventSelection === id) {
      setPreventSelection(false);
      if (timer.current) {
        clearTimeout(timer.current);
      }
    }
  }, [id, preventSelection, setPreventSelection]);

  /**
   * Use the mouse-move event to determine if the user is
   * moving their cursor in the direction of a submenu. We record
   * the previous mouse coordinates and compare them with the
   * current mouse coordinates to determine the angle of movement.
   */

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled) {
        return;
      }

      if (previousMouseCoordinates.current) {
        if (popoverRef.current) {
          // This is a bit naive. We are reading the popper position
          // from the DOM. Ideally we'd pass this position from
          // popperjs through react state instead.
          const placement = popoverRef.current.dataset.position || '';
          const currentBounds = popoverRef.current.getBoundingClientRect();
          let isLeft = placement.includes('left');
          // Fallback/override using geometry if attribute is missing or stale
          if (!isLeft && currentBounds.right <= e.clientX) {
            isLeft = true;
          }

          boundsRef.current = {
            left: currentBounds.left,
            top: currentBounds.top,
            bottom: currentBounds.bottom,
            right: currentBounds.right,
            isLeft,
          };
        }

        // Both Firefox and Chrome only update the position of mouse coordinates
        // when they actually change. Safari can actually send the same
        // coordinates in sequential events, so we need to ingore these
        // duplicate events.
        if (
          previousMouseCoordinates.current.x === e.clientX &&
          previousMouseCoordinates.current.y === e.clientY
        ) {
          return;
        }

        isMovingTowardSubmenu.current = determineIfMovingTowardSubmenu(
          previousMouseCoordinates.current,
          e,
          boundsRef.current
        );
      }

      previousMouseCoordinates.current = { x: e.clientX, y: e.clientY };
      if (isMovingTowardSubmenu.current && !preventSelection) {
        setPreventSelection(id);
      } else if (!isMovingTowardSubmenu.current && preventSelection === id) {
        setPreventSelection(false);
      }
    },
    [enabled, id, popoverRef, preventSelection, setPreventSelection]
  );

  /**
   * When the cursor leaves and we have prevented selection during
   * mouse-move, then set a timer before we enable selection.
   */

  const onMouseLeave = useCallback(() => {
    previousMouseCoordinates.current = null;
    boundsRef.current = null;

    if (preventSelection === id) {
      timer.current = window.setTimeout(() => {
        setPreventSelection(false);
      }, delay);
    }
  }, [id, preventSelection, delay, setPreventSelection]);

  return {
    isMovingTowardSubmenu,
    onMouseMove,
    onMouseLeave,
    onMouseEnter,
    preventSelection,
  };
}

export const testExports = {
  determineIfMovingTowardSubmenu,
};
